import React, { useMemo } from 'react';
import tw, { css, styled } from 'twin.macro';
import { Sparkle, UploadSimple } from '@phosphor-icons/react';
import { AdPlacement } from 'types/adTypes';
import useAdEditorContext from 'hooks/context/ad-editor-context';
import useLoadVideo from 'hooks/load-video';
import Divider from 'components/divider/Divider';
import useVideoGenContext from 'hooks/context/video-gen-context';
import { VIDEO_LOCKED_MESSAGE } from 'utils/videoGenerationLocks';
import { AdEditorSideBarTabs } from 'views/ad-builder/AdBuilder';
import { useFeatureFlagEnabled } from 'posthog-js/react';

// TODO: check if this is duplicate code - StencilCanvas.tsx already has this
export const AdEditorVideoContainer: React.FC = () => {
  const {
    currentVariant,
    uploadMode,
    videoRef,
    setActiveTab,
    canvasWidth,
    canvasHeight,
  } = useAdEditorContext();

  const hasValidSrc = useMemo(
    () => !!currentVariant?.path?.path,
    [currentVariant?.path]
  );

  const src = useMemo(
    () => (hasValidSrc ? `${currentVariant?.path?.path}#t=0.1` : '#'),
    [hasValidSrc, currentVariant?.path]
  );

  useLoadVideo({ videoRef });

  const { videoGen, videoLocks } = useVideoGenContext();

  const hasT1 = useFeatureFlagEnabled('video-track-1-scenario');
  // TODO: drop `ai-video` fallback once T1 rollout is complete and the legacy flag is retired.
  const hasLegacyAiVideo = useFeatureFlagEnabled('ai-video');
  const showT1Entry = hasT1 || hasLegacyAiVideo;

  return (
    <VideoContainer
      editMode={uploadMode}
      hasValidSrc={hasValidSrc}
      placement={currentVariant?.placement}
    >
      {currentVariant?.path ? (
        <Video
          loop
          muted
          id="video"
          key={currentVariant?.path?.path}
          placement={currentVariant?.placement}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          ref={videoRef}
          preload="metadata"
        >
          <source id="source" src={src} type="video/mp4" />
        </Video>
      ) : (
        <div tw="h-full w-full flex justify-center absolute z-0 bg-gray-200">
          <div tw="my-auto h-fit items-center">
            <div
              onClick={() => setActiveTab?.(AdEditorSideBarTabs.VISUAL_OPTIONS)}
              tw="flex flex-col items-center justify-center text-gray-500 font-semibold cursor-pointer"
            >
              <UploadSimple weight="bold" size={32} tw="my-1" />
              Click here to upload your video
            </div>

            {/* SYM-6166. Greyed out while a Track 1 render holds the lock, with the reason on
                hover. Kept identical to the copy in StencilCanvas: the two screens showed the same
                thing differently once already. */}
            {showT1Entry && (
              <>
                <Divider dark />
                <div
                  onClick={videoLocks.creatomate ? undefined : videoGen}
                  title={
                    videoLocks.creatomate ? VIDEO_LOCKED_MESSAGE : undefined
                  }
                  css={[
                    tw`flex flex-col items-center mt-2 justify-center font-semibold`,
                    videoLocks.creatomate
                      ? tw`text-gray-400 cursor-not-allowed`
                      : tw`text-gray-500 cursor-pointer`,
                  ]}
                >
                  <Sparkle weight="fill" size={32} tw="my-1" />
                  Generate a video using AI (beta)
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </VideoContainer>
  );
};

const VideoContainer = styled.div<{
  editMode: boolean;
  hasValidSrc: boolean;
  placement?: AdPlacement;
}>`
  ${tw`relative flex flex-col justify-center`}

  ${({ placement }) =>
    placement === AdPlacement.FEED && tw`w-full min-w-[200px] min-h-[200px]`}

  ${({ placement }) =>
    placement && [AdPlacement.STORIES, AdPlacement.REELS].includes(placement)
      ? tw`aspect-[9/16]`
      : tw`aspect-[1080/1350]`}

  ${({ editMode }) => editMode && tw`pt-0 bg-gray-100`}

  ${({ hasValidSrc }) => !hasValidSrc && tw`min-h-[350px]`}
`;

const Video = styled.video<{
  placement: AdPlacement;
  canvasWidth: number;
  canvasHeight: number;
}>`
  ${tw`absolute top-0 left-1/2 -translate-x-1/2 w-full h-full object-contain max-w-full max-h-full`}

  ${({ canvasWidth, canvasHeight }) =>
    css`
      aspect-ratio: ${canvasWidth / canvasHeight};
    `}

  ${({ placement }) =>
    placement === AdPlacement.STORIES && tw`max-w-full max-h-full`}
`;

export default AdEditorVideoContainer;
